import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOptionalProtectedSession } from '../ProtectedRoute';

function getInitials(emailOrUsername: string | null): string {
  if (!emailOrUsername) {
    return 'A';
  }

  const base = emailOrUsername.split('@')[0]?.trim() || emailOrUsername.trim();
  const parts = base.split(/[.\s_-]+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
  }
  return base.slice(0, 2).toUpperCase() || 'A';
}

export function SelaUserMenu() {
  const navigate = useNavigate();
  const session = useOptionalProtectedSession();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuId = React.useId();

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const initials = useMemo(() => getInitials(session?.username || null), [session?.username]);

  if (!session) {
    return (
      <button
        type="button"
        onClick={() => navigate('/login')}
        className="flex h-9 w-9 items-center justify-center rounded-full bg-[#3c82f6] text-sm font-semibold text-white transition-transform hover:scale-[1.03] focus:outline-none focus:ring-2 focus:ring-[#2570ee] focus:ring-offset-2"
        aria-label="Log in"
      >
        {initials}
      </button>
    );
  }

  const { username, logout } = session;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((prev) => !prev)}
        className="flex h-9 w-9 items-center justify-center rounded-full bg-[#3c82f6] text-sm font-semibold text-white transition-transform hover:scale-[1.03] focus:outline-none focus:ring-2 focus:ring-[#2570ee] focus:ring-offset-2"
      >
        {initials}
      </button>

      {open ? (
        <div
          id={menuId}
          role="menu"
          className="absolute right-0 top-full z-20 mt-2 min-w-[220px] rounded-xl border border-[#e1e8f0] bg-white p-2 shadow-[0px_18px_40px_rgba(15,23,42,0.14)]"
        >
          <div className="rounded-lg px-3 py-2">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#647488]">Signed in as</p>
            <p className="mt-1 break-all text-sm font-medium text-[#0f172a]">{username || 'Authenticated user'}</p>
          </div>
          <div className="my-1 h-px bg-[#e7e9ee]" />
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              void logout();
            }}
            className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm font-medium text-[#475669] transition-colors hover:bg-slate-50 hover:text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#2570ee] focus:ring-offset-2"
          >
            Log out
          </button>
        </div>
      ) : null}
    </div>
  );
}
